import { Text, Badge, ActionIcon, Flex } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { format } from 'date-fns';
import {
  EnrollmentSummary,
  PAY_INTERVAL_LABEL,
  TICKET_TYPE,
} from '#/components/ui/tickets/enrollments/model';
import { TICKET_TYPE_CONFIG } from '#/components/ui/tickets/config';
import { ComponentProps, useMemo, useState, useEffect } from 'react';
import { IconEdit } from '@tabler/icons-react';
import {
  PromiseEditIntervalModal,
  usePromiseEditIntervalModal,
} from '#/components/ui/tickets/enrollments/components/EditIntervalModal';
import { useUpdateEtcEnrollment } from '#/components/ui/tickets/enrollments/endpoints/mutateEnrollment';
import useStaffInfo from '#/hooks/staff/use-staff-info';
import { VEHICLE_TYPE_LABEL } from '../../../compulsory-insurance-penalty/model';

const IntervalUpdateButton = ({
  id,
  type,
  email,
  payInterval,
}: {
  id: EnrollmentSummary['id'];
  type: EnrollmentSummary['type'];
  email: EnrollmentSummary['email'];
  payInterval: EnrollmentSummary['payInterval'];
}) => {
  const { props, open } = usePromiseEditIntervalModal();
  const { mutate: mutateEtc } = useUpdateEtcEnrollment();
  const { staff } = useStaffInfo();

  const isEditable = !!staff?.permissions.COCKPIT_ETC_AUTO_PAY_ENROLLMENT.write;

  return (
    <>
      <ActionIcon
        variant="transparent"
        aria-label="Settings"
        size={'sm'}
        disabled={!isEditable}
        onClick={async () => {
          const result = await open({ payInterval });

          if (!result) {
            return;
          }

          mutateEtc({
            enrollmentId: id,
            payInterval: result.payInterval,
          });
        }}
      >
        <IconEdit />
      </ActionIcon>
      <PromiseEditIntervalModal email={email} type={type} {...props} />
    </>
  );
};

const columns: DataTableColumn<EnrollmentSummary>[] = [
  {
    title: 'Email',
    accessor: 'email',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '20%',
    render: ({ email }) => <Text size="sm">{email ?? '-'}</Text>,
  },
  {
    title: '車牌號碼',
    accessor: 'plateNumber',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '10%',
    render: ({ plateNumber }) => <Text size="sm">{plateNumber}</Text>,
  },

  {
    title: '證件號碼 / 統編',
    accessor: 'idNumber',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '12%',
    render: ({ idNumber, taxIdNumber }) => (
      <Text size="sm">{idNumber ?? taxIdNumber ?? '-'}</Text>
    ),
  },
  {
    title: '車種',
    accessor: 'vehicleType',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '12%',
    render: ({ vehicleType }) => (
      <Text size="sm">
        {vehicleType ? VEHICLE_TYPE_LABEL[vehicleType] : '-'}
      </Text>
    ),
  },
  {
    title: '申請服務',
    accessor: 'type',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '12%',
    render: ({ type }) => (
      <Text size="sm">{TICKET_TYPE_CONFIG[type].name}</Text>
    ),
  },
  {
    title: '狀態',
    accessor: 'state',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '10%',
    render: ({ state }) =>
      state === 'ACTIVE' ? (
        <Badge size="lg" variant="light" color="green">
          使用中
        </Badge>
      ) : state === 'INACTIVE' ? (
        <Badge size="lg" variant="light" color="red">
          已停用
        </Badge>
      ) : null,
  },
  {
    title: '查詢週期',
    accessor: 'payInterval',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '12%',
    render: ({ payInterval, type, id, email }) => {
      return (
        <Flex gap={6} align={'center'}>
          <Badge size="lg" variant="light">
            {PAY_INTERVAL_LABEL[payInterval]}
          </Badge>
          {type === TICKET_TYPE.ETC_TICKETS && (
            <IntervalUpdateButton
              id={id}
              email={email}
              type={type}
              payInterval={payInterval}
            />
          )}
        </Flex>
      );
    },
  },
  {
    title: '申請時間',
    accessor: 'createDateTime',
    titleStyle: { color: 'gray', fontWeight: 'normal' },
    width: '14%',
    render: ({ createDateTime }) => (
      <Text size="sm">
        {format(new Date(createDateTime), 'yyyy-MM-dd HH:mm')}
      </Text>
    ),
  },
];

type EnrollmentSummaryListProps = Required<
  Pick<ComponentProps<typeof DataTable>, 'fetching'>
>;

export const EnrollmentSummaryList = (
  props: EnrollmentSummaryListProps & {
    records: EnrollmentSummary[];
  },
) => {
  const [page, setPage] = useState(1);

  const totalRecords = props.records.length;
  const recordsPerPage = 14;
  const onPageChange = setPage;

  const records = useMemo(() => {
    const from = recordsPerPage * (page - 1);
    const to = recordsPerPage * page - 1;
    return props.records.slice(from, to);
  }, [props.records, page]);

  useEffect(() => {
    setPage(1);
  }, [props.records]);

  return (
    <DataTable
      idAccessor="id"
      highlightOnHover
      minHeight={400}
      columns={columns}
      paginationText={({
        from,
        to,
        totalRecords,
      }: {
        from: number;
        to: number;
        totalRecords: number;
      }) => `顯示 ${from} - ${to} 筆申請資料，共 ${totalRecords} 筆`}
      verticalSpacing="sm"
      noRecordsText="沒有符合條件的申請單"
      withTableBorder={false}
      totalRecords={totalRecords}
      recordsPerPage={recordsPerPage}
      page={page}
      records={records}
      fetching={props.fetching}
      onPageChange={onPageChange}
    />
  );
};
